import { ChatHistoryHelper } from './chat-history.helper';

/**
 * GetChatResponseHelper
 *
 * Extracted from ChatbotexpandComponent. Holds `getChatResponse`, which loads a chat
 * message's analytical answer (from session cache, incremental load, or the API) and
 * renders it via `sendInputMessage`.
 *
 * Follows the existing `comp: any` helper pattern — the component keeps a thin async
 * wrapper method (bound in the template) that delegates here, so all call sites remain
 * unchanged and behavior is preserved (verbatim `this.` -> `comp.` transcription).
 */
export class GetChatResponseHelper {
    static async getChatResponse(comp: any, messageId: string | undefined, showSingleMessageOnly: boolean = false, autoScroll: boolean = true): Promise<void> {
        if (!messageId) return;
        comp.isChatHistoryOpen = false;
        comp.isSavedQuestionsOpen = false;
        comp.isScrollingToTarget = true;
        comp.scrollSpacerHeight = 0; // Clear any active new-question spacer
        comp.currentView = 'chat';

        // Ensure metadata is ready before processing the response (prevents empty dropdowns)
        await comp.preFetchMetadata();

        comp.isNewChatStarted = false;
        comp.abandoningSessionId = '';
        comp.OutputData = [];
        comp.CrossTabOutPutData = [];
        comp.showSnapshot = false;
        comp.displayTable = false;
        comp.setFailedCaseState(false);
        comp.isCreateBtnClick = false;
        comp.insightsVal = '';
        comp.insightsStreamingText = '';
        comp.insightsStreamingPoints = [];
        comp.isselectionsummarycollapse = false;
        comp.isselectionsummarycollapse2 = false;
        comp._configService.standardSnapOutputData = [];
        comp.MainResult = undefined;
        comp.filteredWidgetData = undefined;
        comp._configService.updateSignificanceValue('None');
        comp._configService.updateConfedenceValue('None');
        comp._configService.IndexSelected = false;
        comp._configService.baseSelected = 'Total Base';
        comp._configService.isTotalBase = true;
        const chat = comp.allChatList.find((c: any) => c.ChatHistory && c.ChatHistory.some((h: any) => h.MessageId === messageId));

        if (chat) {

            const targetMessage = chat.ChatHistory?.find((h: any) => h.MessageId === messageId);
            if (targetMessage) {
            }
        }

        if (chat) {
            comp.selectedChatId = chat.ChatId || null;
            comp.selectedMessageId = messageId;
            comp.guidChatId = chat.ChatId || '';
            comp.newChatId = chat.ChatId || null;

            if (!showSingleMessageOnly) {
                comp.chatPreviewBlocks = (chat.ChatHistory || []).map((h: any) =>
                    ChatHistoryHelper.createChatPreviewBlock(h, comp.defaultQuestionAnswer, comp.DefaultWeight, comp.DefaultBase, comp.selectedWeight)
                );
            }
        } else {
            comp.selectedChatId = messageId;
            comp.guidChatId = messageId;
        }

        // SESSION VALIDATION FOR OLD HISTORY
        if (comp.guidChatId && comp.validatedSessionId !== comp.guidChatId) {
            comp.isCacheGenerated = false; // Reset flag for the new (old) session
            comp.getBearerToken().then((token: any) => {
                comp.apiService.accessCheck(
                    comp.selected_client || '',
                    comp.selected_study || '',
                    token,
                    comp.guidChatId || ''
                ).subscribe({
                    next: (res: any) => {
                        if (res && res.modules_access) {
                            comp.modulesAccess = res.modules_access;
                            comp.validatedSessionId = comp.guidChatId;

                            const activeModules: any = {};
                            for (const [key, value] of Object.entries(comp.modulesAccess)) {
                                if ((value as any).has_access) {
                                    activeModules[key] = value;
                                }
                            }

                            comp.apiService.generateRestrictedCache(
                                comp.selected_client || '',
                                comp.selected_study || '',
                                token,
                                comp.guidChatId,
                                activeModules
                            ).subscribe({
                                next: () => {
                                    comp.isCacheGenerated = true;
                                },
                                error: (err: any) => {
                                    console.error('[getChatResponse] ❌ Cache failed:', err);
                                }
                            });
                        }
                    },
                    error: (err: any) => {
                        console.error('[getChatResponse] ❌ Access check failed:', err);
                    }
                });
            });
        }

        comp.isChatHistoryOpen = false;
        comp.isSavedQuestionsOpen = false;
        comp.inputValue = '';

        if (chat && !showSingleMessageOnly && comp.selectedChatId) {
            comp.pendingMessageId = messageId;
            comp.isPreFetchingAnswers = false; // Reset to allow fetching
            comp.loadAnswersIncrementally(chat, messageId);
            return;
        }

        const cachedBlock = comp.chatPreviewBlocks.find((b: any) => b.messageId?.toLowerCase() === messageId?.toLowerCase());
        if (cachedBlock && cachedBlock.fullAnswer) {
            comp.sendInputMessage(cachedBlock.fullAnswer, true, cachedBlock);
            if (autoScroll) {
                comp.scrollToMessage(messageId, 'start');
            }
            return;
        }

        if (comp.isPreFetchingAnswers) {
            comp.pendingMessageId = messageId;
            // Loader stays on. loadAllAnswersForChat will resolve this.
            return;
        }

        console.warn('[getChatResponse] ⚠️ Analytical answer not found in session cache for:', messageId);

        comp.apiService.getChatResponse(messageId).subscribe({
            next: (res: string) => {
                const apiResponse = JSON.parse(res);

                if (apiResponse.HasValidData && apiResponse.Data) {
                    let historyItem: any;
                    const parsedData = JSON.parse(apiResponse.Data);

                    if (Array.isArray(parsedData)) {
                        historyItem = parsedData[0];
                    } else {
                        historyItem = parsedData;
                    }

                    let answerData = historyItem?.Answer;
                    if (typeof answerData === 'string') {
                        try {
                            answerData = JSON.parse(answerData);
                        } catch (e) {
                            console.error('[getChatResponse API] Failed to parse answer data:', e);
                        }
                    }

                    // Cache it for this session
                    if (cachedBlock) {
                        cachedBlock.fullAnswer = answerData;
                        const parsed = comp.parseChatResponse(answerData);
                        cachedBlock.parsedData = parsed;
                        cachedBlock.mcode = parsed.member_responses?.final_mcode || parsed.AgentPayload?.mcode || parsed.GetMcode?.mcode || 0;
                        cachedBlock.isBrandAnalysis = (cachedBlock.mcode === 2);
                        cachedBlock.analyticalData = parsed;
                        comp.populateBlockInsights(cachedBlock, parsed);
                    }

                    comp.sendInputMessage(answerData, true, cachedBlock);
                    if (autoScroll) {
                        comp.scrollToMessage(messageId, 'start');
                    }
                } else {
                    console.warn('[getChatResponse API] No valid data returned for messageId:', messageId);
                    comp.isScrollingToTarget = false;
                    comp._loaderService.hideLoader();
                }
            },
            error: (err: any) => {
                comp.chatHistory = [];
                comp.isScrollingToTarget = false;
                comp._loaderService.hideLoader();
            }
        });
    }
}
